import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Position } from '../lib/api';
import { fmt } from '../lib/format';
import { buildMonths, monthLabel } from '../lib/months';

// ── Helpers ──────────────────────────────────────────────────────

/** Invest coin: PUT=USDT, CALL=BTC */
function investCoin(optType: string): 'USDT' | 'BTC' {
  return optType === 'PUT' ? 'USDT' : 'BTC';
}

/** Est profit dalam USDT — berdasarkan Binance DCD formula */
function estProfitUsdt(p: Position, spotPrice: number): number | null {
  const strike = Number(p.strike_price) || 0;
  const apr = Number(p.apr) || 0;
  const subs = new Date(p.subscribed_at);
  const settle = new Date(p.settle_date);
  const days = Math.max(1, Math.round((settle.getTime() - subs.getTime()) / 86_400_000));

  if (p.option_type === 'PUT') {
    return strike > 0 && apr > 0 ? strike * (apr / 100) * (days / 365) : null;
  }
  return spotPrice > 0 && apr > 0 ? spotPrice * (apr / 100) * (days / 365) : null;
}

/** Duration dalam hari */
function durationDays(p: Position): number {
  const subs = new Date(p.subscribed_at);
  const settle = new Date(p.settle_date);
  if (Number.isNaN(subs.getTime()) || Number.isNaN(settle.getTime())) return 0;
  return Math.max(1, Math.round((settle.getTime() - subs.getTime()) / 86_400_000));
}

/** Sisa hari sampai settle (untuk posisi active) */
function daysUntilSettle(p: Position): number | null {
  if (p.status !== 'active') return null;
  const now = Date.now();
  const settleMs = new Date(p.settle_date).getTime();
  if (Number.isNaN(settleMs)) return null;
  const diff = Math.ceil((settleMs - now) / 86_400_000);
  return Math.max(0, diff);
}

/** Status badge: 3 kondisi */
function statusInfo(p: Position): { label: string; level: 'ok' | 'wait' | 'pending' } {
  if (p.status === 'active') {
    const days = daysUntilSettle(p);
    // Kalau settle_date sudah lewat tapi status masih active (belum sync), tampilkan Selesai
    if (days !== null && days <= 0) {
      return { label: 'Selesai', level: 'ok' };
    }
    return {
      label: days !== null ? `Menunggu ${days} hari` : 'Menunggu',
      level: 'wait',
    };
  }

  // Settled: bandingkan settle_asset dengan invest_coin
  // Kalau sama → koin kembali → Selesai
  // Kalau beda → coin terjual → Pending
  const coin = investCoin(p.option_type);
  const settleAsset = (p.settle_asset || '').toUpperCase();

  // Kalau settle_asset ada, bandingkan langsung
  if (settleAsset) {
    if (settleAsset === coin) {
      return { label: 'Selesai', level: 'ok' };
    }
    return { label: `${coin} Terjual`, level: 'pending' };
  }

  // Fallback: kalau settle_asset belum ada (lama), pakai settlement_pnl
  const pnl = p.settlement_pnl != null ? Number(p.settlement_pnl) : null;
  if (pnl !== null) {
    return { label: `${coin} Terjual`, level: 'pending' };
  }
  return { label: 'Selesai', level: 'ok' };
}

// ── Component ────────────────────────────────────────────────────

export function PositionsPage() {
  const months = useMemo(() => buildMonths(8), []);
  const [month, setMonth] = useState(months[0]?.value ?? '');
  const queryClient = useQueryClient();

  // Fetch positions
  const { data, isLoading } = useQuery({
    queryKey: ['positions', month],
    queryFn: () => api.get<{ positions: Position[]; count: number }>(
      `/api/binance/positions?month=${month}`,
    ),
    refetchInterval: 30_000,
  });

  // Sync dari Binance
  const syncMutation = useMutation({
    mutationFn: () => api.post<{ refreshed: number; total: number }>(
      '/api/binance/positions/refresh',
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['positions'] }),
  });

  // Fetch spot price
  const { data: btcPrice } = useQuery({
    queryKey: ['price', 'BTCUSDT'],
    queryFn: () => api.get<{ symbol: string; price: number }>('/api/binance/price?symbol=BTCUSDT'),
    refetchInterval: 30_000,
  });

  // Fetch USDT/IDR
  const { data: usdtIdr } = useQuery({
    queryKey: ['price', 'USDTIDR'],
    queryFn: () => api.get<{ symbol: string; price: number }>('/api/binance/price?symbol=USDTIDR'),
    refetchInterval: 60_000,
  });

  const spotMap: Record<string, number> = { BTCUSDT: btcPrice?.price ?? 0 };
  const usdtIdrRate = usdtIdr?.price ?? 0;

  const positions = data?.positions || [];

  // Split PUT / CALL
  const putPositions = useMemo(() => positions.filter((p) => p.option_type === 'PUT'), [positions]);
  const callPositions = useMemo(() => positions.filter((p) => p.option_type === 'CALL'), [positions]);

  // ── Summary calculations ─────────────────────────────────────

  // Modal total dalam IDR
  const totalModalIdr = positions.reduce((s, p) => {
    const invested = Number(p.invested) || 0;
    if (investCoin(p.option_type) === 'USDT') {
      return s + invested * usdtIdrRate;
    }
    return s + invested * (spotMap.BTCUSDT || 0) * usdtIdrRate;
  }, 0);

  // Est profit total dalam IDR
  const totalProfitIdr = positions.reduce((s, p) => {
    const est = estProfitUsdt(p, spotMap[p.underlying] || 0);
    return s + (est || 0) * usdtIdrRate;
  }, 0);

  // Countdown posisi aktif terdekat
  const activePositions = positions.filter((p) => p.status === 'active');
  const nearestActive = activePositions.length > 0
    ? activePositions.reduce((min, p) => {
        const d = daysUntilSettle(p);
        return d !== null && d < min ? d : min;
      }, Infinity)
    : null;

  // ── Render ──────────────────────────────────────────────────

  return (
    <div className="gl-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--gl-text)', letterSpacing: '-0.3px' }}>
            Positions — History
          </div>
          <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', marginTop: 4 }}>
            Trading history DCD Binance · {monthLabel(month)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{
              background: 'var(--gl-surface)', color: 'var(--gl-text)', border: '1px solid var(--gl-border)',
              borderRadius: 8, padding: '6px 10px', fontSize: 13, cursor: 'pointer',
            }}
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <button
            className="glass-btn glass-btn--primary"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
          >
            <span className="glass-btn-inner">
              {syncMutation.isPending ? '⏳ Syncing…' : '🔄 Sync'}
            </span>
          </button>
        </div>
      </div>

      {/* Sync status */}
      {syncMutation.isSuccess && syncMutation.data && (
        <div style={{
          padding: '8px 12px', marginBottom: 16, borderRadius: 8,
          background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
          fontSize: 13, color: 'var(--gl-emerald)',
        }}>
          ✅ Sync {syncMutation.data.refreshed} posisi dari Binance
        </div>
      )}
      {syncMutation.isError && (
        <div style={{
          padding: '8px 12px', marginBottom: 16, borderRadius: 8,
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          fontSize: 13, color: 'var(--gl-rose)',
        }}>
          ❌ Sync gagal: {(syncMutation.error as Error)?.message}
        </div>
      )}

      {/* ── Summary Chips ── */}
      {positions.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <SummaryChip
            label="💰 Modal Total"
            value={usdtIdrRate > 0 ? fmt.idr(totalModalIdr) : '—'}
            color="var(--gl-text)"
          />
          <SummaryChip
            label="📈 Est. Profit"
            value={usdtIdrRate > 0 ? fmt.idr(totalProfitIdr) : '—'}
            color="var(--gl-emerald)"
          />
          {nearestActive !== null ? (
            <SummaryChip
              label="⏳ Pending"
              value={`${nearestActive} hari`}
              color="var(--gl-cyan)"
            />
          ) : (
            <SummaryChip
              label="⏳ Pending"
              value="Semua selesai"
              color="var(--gl-text-faint)"
            />
          )}
        </div>
      )}

      {/* Loading / Empty */}
      {isLoading ? (
        <div className="g-card rvl"><div className="gl-empty">Memuat...</div></div>
      ) : positions.length === 0 ? (
        <div className="g-card rvl">
          <div className="gl-empty" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <span>Tidak ada posisi untuk {monthLabel(month)}</span>
            <button
              className="glass-btn glass-btn--primary"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              <span className="glass-btn-inner">
                {syncMutation.isPending ? '⏳ Syncing…' : '🔄 Sync dari Binance'}
              </span>
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* ═══════ PUT — Beli BTC di Harga Bawah ═══════ */}
          <SectionHeader
            type="PUT"
            title="Beli BTC di Harga Bawah"
            desc="Invest USDT → Dapat BTC kalau spot ≤ strike"
            count={putPositions.length}
            modalUsdt={putPositions.reduce((s, p) => s + Number(p.invested || 0), 0)}
            usdtIdrRate={usdtIdrRate}
          />
          {putPositions.length > 0 ? (
            <PositionTable positions={putPositions} spotMap={spotMap} usdtIdrRate={usdtIdrRate} />
          ) : (
            <div className="g-card rvl" style={{ marginBottom: 16 }}>
              <div className="gl-empty">Tidak ada posisi PUT bulan ini</div>
            </div>
          )}

          {/* ═══════ CALL — Jual BTC di Harga Atas ═══════ */}
          <SectionHeader
            type="CALL"
            title="Jual BTC di Harga Atas"
            desc="Invest BTC → Dapat USDT kalau spot ≥ strike"
            count={callPositions.length}
            modalBtc={callPositions.reduce((s, p) => s + Number(p.invested || 0), 0)}
            spotMap={spotMap}
            usdtIdrRate={usdtIdrRate}
          />
          {callPositions.length > 0 ? (
            <PositionTable positions={callPositions} spotMap={spotMap} usdtIdrRate={usdtIdrRate} />
          ) : (
            <div className="g-card rvl" style={{ marginBottom: 16 }}>
              <div className="gl-empty">Tidak ada posisi CALL bulan ini</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────

function SummaryChip({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{
      background: 'var(--gl-surface)', border: '1px solid var(--gl-border)', borderRadius: 10,
      padding: '8px 14px', minWidth: 140,
    }}>
      <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color, fontFamily: 'var(--font-mono)' }}>
        {value}
      </div>
    </div>
  );
}

function SectionHeader({
  type, title, desc, count,
  modalUsdt, modalBtc, spotMap, usdtIdrRate,
}: {
  type: 'PUT' | 'CALL';
  title: string;
  desc: string;
  count: number;
  modalUsdt?: number;
  modalBtc?: number;
  spotMap?: Record<string, number>;
  usdtIdrRate: number;
}) {
  const isPut = type === 'PUT';
  const modalIdr = isPut
    ? (modalUsdt || 0) * usdtIdrRate
    : (modalBtc || 0) * (spotMap?.BTCUSDT || 0) * usdtIdrRate;

  return (
    <div className="g-card rvl" style={{
      marginBottom: 4,
      borderColor: isPut ? 'rgba(6,182,212,0.3)' : 'rgba(245,158,11,0.3)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span className={`gl-badge gl-badge--${isPut ? 'info' : 'warn'}`}>{type}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--gl-text)' }}>{title}</span>
        <span style={{ fontSize: 12, color: 'var(--gl-text-muted)' }}>— {desc}</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--gl-text-faint)' }}>
          {count} posisi
          {usdtIdrRate > 0 && <span style={{ marginLeft: 6, color: 'var(--gl-amber)' }}>{fmt.idr(modalIdr)}</span>}
        </span>
      </div>
    </div>
  );
}

function PositionTable({
  positions, spotMap, usdtIdrRate,
}: {
  positions: Position[];
  spotMap: Record<string, number>;
  usdtIdrRate: number;
}) {
  return (
    <div className="g-card rvl" style={{ marginBottom: 16 }}>
      <table className="glt">
        <thead>
          <tr>
            <th>Tanggal Subs</th>
            <th>Strike</th>
            <th>Durasi</th>
            <th>Modal</th>
            <th>APR</th>
            <th>Est. Profit</th>
            <th>Settlement</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {positions.map((p) => {
            const spot = spotMap[p.underlying] || 0;
            const est = estProfitUsdt(p, spot);
            const estIdr = est != null ? est * usdtIdrRate : null;
            const days = durationDays(p);
            const coin = investCoin(p.option_type);
            const invested = Number(p.invested) || 0;
            const modalIdr = coin === 'USDT'
              ? invested * usdtIdrRate
              : invested * spot * usdtIdrRate;
            const st = statusInfo(p);

            return (
              <tr key={p.id} style={p.status === 'settled' ? { opacity: 0.75 } : undefined}>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                  {fmt.date(p.subscribed_at)}
                </td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600 }}>
                  {fmt.number(p.strike_price, 0)}
                </td>
                <td>{days}h</td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--gl-amber)' }}>
                  {usdtIdrRate > 0 ? fmt.idr(modalIdr) : '—'}
                </td>
                <td style={{ color: 'var(--gl-emerald)', fontWeight: 600 }}>
                  {fmt.percent(p.apr, 2)}
                </td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--gl-emerald)' }}>
                  {estIdr != null ? `≈ ${fmt.idr(estIdr)}` : '—'}
                </td>
                <td style={{ fontSize: 12 }}>
                  {fmt.dateDay(p.settle_date)}
                </td>
                <td>
                  {st.level === 'pending' ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 10px', borderRadius: 6,
                      background: 'rgba(239,68,68,0.15)', color: 'var(--gl-rose)',
                      border: '1px solid rgba(239,68,68,0.3)', fontSize: 12, fontWeight: 600,
                    }}>
                      🔴 {st.label}
                    </span>
                  ) : st.level === 'wait' ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 10px', borderRadius: 6,
                      background: 'rgba(59,130,246,0.12)', color: 'var(--gl-cyan)',
                      border: '1px solid rgba(59,130,246,0.25)', fontSize: 12, fontWeight: 600,
                    }}>
                      ⏳ {st.label}
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 10px', borderRadius: 6,
                      background: 'rgba(16,185,129,0.12)', color: 'var(--gl-emerald)',
                      border: '1px solid rgba(16,185,129,0.25)', fontSize: 12, fontWeight: 600,
                    }}>
                      ✓ {st.label}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
